import { useState, useCallback } from 'react';

const useRequestPeriodEditorChangeHandlers = ({
  onPeriodClose,
  onPeriodClick,
  requestPeriodEditTarget,
  setRequestPeriodEditTarget
}) => {
  const [allRequestPeriods, setAllRequestPeriods] = useState(null);

  const handleRequestPeriodClick = ({
    event,
    startDate,
    endDate,
    resourceRequest,
    totalHours,
    totalScheduleHours,
    requestPeriods
  }) => {
    if (event.currentTarget) {
      onPeriodClick(resourceRequest);
      setAllRequestPeriods(requestPeriods);

      setRequestPeriodEditTarget({
        anchorEl: event.currentTarget,
        resourceRequest,
        startDate,
        endDate,
        totalScheduleHours,
        totalHours
      });
    }
  };

  const handleRequestPeriodClose = useCallback(
    ({ request }) => {
      if (onPeriodClose && request) {
        onPeriodClose({ request });
      }
      setRequestPeriodEditTarget(null);
    },
    [onPeriodClose, setRequestPeriodEditTarget]
  );

  const newPeriod = (periodDetails, newAnchor, newSummary, request) => ({
    ...periodDetails,
    anchorEl: newAnchor,
    startDate: newSummary.startDate,
    endDate: newSummary.endDate,
    totalHours: newSummary.totalHours,
    totalScheduleHours: newSummary.totalScheduleHours,
    resourceRequest: request
  });

  const getPeriod = useCallback(
    (
      periodDetails,
      request,
      sibling,
      allocatedValue,
      addSubtract,
      overrideRequestPeriods
    ) => {
      const requestPeriods = overrideRequestPeriods || [...allRequestPeriods];
      const currIndex = requestPeriods.findIndex(
        obj =>
          obj.startDate <= periodDetails.startDate &&
          obj.endDate >= periodDetails.endDate
      );

      const newIndex = currIndex + addSubtract;

      if (newIndex < 0 || newIndex > requestPeriods.length - 1) {
        requestPeriods[currIndex].totalHours = allocatedValue;
        setAllRequestPeriods(requestPeriods);
        setRequestPeriodEditTarget(
          newPeriod(periodDetails, null, requestPeriods[currIndex], request)
        );
      } else {
        requestPeriods[currIndex].totalHours = allocatedValue;
        const newSummary = requestPeriods[newIndex];
        const newAnchor = sibling && sibling;

        setRequestPeriodEditTarget(
          newPeriod(periodDetails, newAnchor, newSummary, request)
        );

        setAllRequestPeriods(requestPeriods);
      }
    },
    [allRequestPeriods, setRequestPeriodEditTarget]
  );

  const getUpdatedRequestPeriodsWithPeriodDetails = useCallback(
    ({ foundRequestPeriod }) => {
      const requestPeriods = [...allRequestPeriods].filter(
        period => !period.isPartialPeriod
      );

      const selectedRequestPeriodIndex = requestPeriods.findIndex(
        period =>
          foundRequestPeriod.startDate >= period.startDate &&
          foundRequestPeriod.endDate <= period.endDate
      );

      const {
        anchorEl: { parentElement }
      } = requestPeriodEditTarget;

      const parentDiv = parentElement.closest('.resourceRequestDraft');
      const allocationChildren = parentDiv.getElementsByClassName(
        'resourceRequestOverlay'
      );

      return { allocationChildren, requestPeriods, selectedRequestPeriodIndex };
    },
    [allRequestPeriods, requestPeriodEditTarget]
  );

  const setNextPeriod = useCallback(
    (periodDetails, request, allocatedValue) => {
      const foundRequestPeriod = allRequestPeriods.find(
        period =>
          period.startDate === periodDetails.startDate &&
          period.endDate === periodDetails.endDate
      );

      if (foundRequestPeriod.isPartialPeriod) {
        const {
          allocationChildren,
          requestPeriods,
          selectedRequestPeriodIndex
        } = getUpdatedRequestPeriodsWithPeriodDetails({ foundRequestPeriod });

        return getPeriod(
          periodDetails,
          request,
          allocationChildren[selectedRequestPeriodIndex],
          allocatedValue,
          0,
          requestPeriods
        );
      }

      const {
        allocationChildren,
        requestPeriods,
        selectedRequestPeriodIndex
      } = getUpdatedRequestPeriodsWithPeriodDetails({ foundRequestPeriod });

      return getPeriod(
        periodDetails,
        request,
        allocationChildren[selectedRequestPeriodIndex + 1],
        allocatedValue,
        1,
        requestPeriods
      );
    },
    [allRequestPeriods, getPeriod, getUpdatedRequestPeriodsWithPeriodDetails]
  );

  const setPreviousPeriod = useCallback(
    (periodDetails, request, allocatedValue) => {
      const foundRequestPeriod = allRequestPeriods.find(
        period =>
          period.startDate === periodDetails.startDate &&
          period.endDate === periodDetails.endDate
      );

      if (foundRequestPeriod.isPartialPeriod) {
        const {
          allocationChildren,
          requestPeriods,
          selectedRequestPeriodIndex
        } = getUpdatedRequestPeriodsWithPeriodDetails({ foundRequestPeriod });

        return getPeriod(
          periodDetails,
          request,
          allocationChildren[selectedRequestPeriodIndex],
          allocatedValue,
          0,
          requestPeriods
        );
      }

      const {
        allocationChildren,
        requestPeriods,
        selectedRequestPeriodIndex
      } = getUpdatedRequestPeriodsWithPeriodDetails({ foundRequestPeriod });

      return getPeriod(
        periodDetails,
        request,
        allocationChildren[selectedRequestPeriodIndex - 1],
        allocatedValue,
        -1,
        requestPeriods
      );
    },
    [allRequestPeriods, getPeriod, getUpdatedRequestPeriodsWithPeriodDetails]
  );

  return {
    handleRequestPeriodClick,
    handleRequestPeriodClose,
    setPreviousPeriod,
    setNextPeriod
  };
};

export default useRequestPeriodEditorChangeHandlers;
